import { memo } from 'react';
import css from './IconButton.module.scss';
import classNames from 'classnames';
import { GMTooltip } from '../../../Tooltip/Tooltip';
import { IconButton as MuiIconButton, SvgIcon } from '@mui/material';
import { GoMotoComponent } from '../../../../API/GoMoto.types';
import { GMIconButtonProps } from '..';

/**
 * Wrapper component for an icon that handles color states and tooltip
 *
 * @param color - {@link GMIconButtonProps.color color} description. (default: `'dark'`)
 * @param showTooltipWhenDisabled - {@link GMIconButtonProps.showTooltipWhenDisabled showTooltipWhenDisabled} description.
 * @param tooltip - {@link GMIconButtonProps.tooltip tooltip} description.
 * @param className - {@link GMIconButtonProps.className className} description.
 * @param disabled - {@link GMIconButtonProps.disabled disabled} description.
 * @param active - {@link GMIconButtonProps.active active} description.
 * @param disableClick - {@link GMIconButtonProps.disableClick disableClick} description.
 * @param colorPathStroke - {@link GMIconButtonProps.colorPathStroke colorPathStroke} description.
 * @param colorPathFill - {@link GMIconButtonProps.colorPathFill colorPathFill} description.
 * @param colorPolygonStroke - {@link GMIconButtonProps.colorPolygonStroke colorPolygonStroke} description.
 * @param colorEllipseStroke - {@link GMIconButtonProps.colorEllipseStroke colorEllipseStroke} description.
 * @param onClick - {@link GMIconButtonProps.onClick onClick} description.
 * @param notTabbable - {@link GMIconButtonProps.notTabbable notTabbable} description.
 * @param disableRipple - {@link GMIconButtonProps.disableRipple disableRipple} description.
 * @param children - {@link GMIconButtonProps.children children} description.
 */
const IconButton: GoMotoComponent<GMIconButtonProps> = memo(({
    color = 'dark',
    showTooltipWhenDisabled,
    tooltip,
    className,
    disabled,
    active,
    disableClick,
    colorPathStroke,
    colorPathFill,
    colorPolygonStroke,
    colorEllipseStroke,
    onClick,
    notTabbable,
    disableRipple,
    spanClassName,
    children,
}) => {
    return (
        <GMTooltip disabled={disabled && !showTooltipWhenDisabled} text={tooltip}>
            <span className={classNames(css.span, spanClassName)}>
                <MuiIconButton
                    className={classNames(
                        css.root,
                        {
                            [css.light]: color === 'light',
                            [css.primary]: color === 'primary',
                            [css.secondary]: color === 'secondary',
                            [css.red]: color === 'red',
                            [css.darkAlt]: color === 'darkAlt',
                            [css.dark]: color === 'dark',
                            [css.disabled]: !!disabled,
                            [css.active]: !!active,
                            [css.noClick]: !!disableClick,
                            [css.colorPathStroke]: !!colorPathStroke,
                            [css.colorPathFill]: !!colorPathFill,
                            [css.colorEllipseStroke]: !!colorEllipseStroke,
                            [css.colorPolygonStroke]: !!colorPolygonStroke,
                        },
                        className,
                    )}
                    onClick={!disabled && !disableClick ? onClick : undefined}
                    disabled={disabled}
                    tabIndex={notTabbable ? -1 : undefined}
                    disableRipple={disableRipple}
                >
                    {children}
                </MuiIconButton>
            </span>
        </GMTooltip>
    );
});

export default IconButton;

/**
 * Wrapper component for an icon that handles color states and tooltip.
 * Similar to GMIconButton but not a button
 */
export const GMIcon: GoMotoComponent<Pick<GMIconButtonProps, 'color' | 'tooltip' | 'colorPathFill' | 'colorPathStroke' | 'children' | 'className' | 'spanClassName'>> = memo(({
    color = 'dark',
    ...props
}) => {
    return (
        <GMTooltip text={props.tooltip}>
            <span className={classNames(css.span, props.spanClassName)}>
                <SvgIcon
                    className={classNames(
                        css.root,
                        color === 'light' ? css.light :
                            color === 'primary' ? css.primary :
                                color === 'secondary' ? css.secondary :
                                    color === 'red' ? css.red :
                                        color === 'darkAlt' ? css.darkAlt :
                                            css.dark,
                        props.colorPathStroke ? css.colorPathStroke : '',
                        props.colorPathFill ? css.colorPathFill : '',
                        props.className,
                    )}
                >
                    {props.children}
                </SvgIcon>
            </span>
        </GMTooltip>
    );
});